import React, { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import { Search, X, ArrowRight, Receipt } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { PRODUCTS, GALLERY } from "../data/products";
import { useI18n } from "../contexts/I18nContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Un code de fiche technique = session_id Stripe (`cs_test_…` / `cs_live_…`)
// OU code brouillon (`DRAFT-XXX`). On détecte pour lancer le lookup backend.
const ORDER_CODE_RE = /^(cs_(?:test|live)_[a-z0-9]{20,}|DRAFT-[A-Z0-9]{6,})$/i;

// Mapping famille de croix → slug de la page produit correspondante.
// Utilisé pour rediriger la recherche design vers le bon configurateur.
const CROSS_FAMILY_TO_PRODUCT_SLUG = {
  dotmatrix: "croix-point-a-point",
  classic: "croix-classique-led",
  // Toutes les autres familles (croix, croix-fleche, ronde, ronde-multi, multi,
  // angle, neon) sont accessibles depuis la page "croix-neon-led" qui héberge
  // l'ensemble des designs personnalisables + customs.
  croix: "croix-neon-led",
  "croix-fleche": "croix-neon-led",
  ronde: "croix-neon-led",
  "ronde-multi": "croix-neon-led",
  multi: "croix-neon-led",
  angle: "croix-neon-led",
  neon: "croix-neon-led",
};

/**
 * Recherche site : bouton loupe → overlay plein écran avec input + résultats
 * groupés (Produits · Galerie · FAQ). Match large sur name/description/features
 * dans les 4 langues + titres galerie + questions/réponses FAQ.
 */
export default function SiteSearch() {
  const { t, lang } = useI18n();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [orderLookup, setOrderLookup] = useState({ status: "idle", data: null, error: null });
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Détecte un code de fiche technique et lance le lookup backend
  useEffect(() => {
    const q = query.trim();
    if (!ORDER_CODE_RE.test(q)) {
      setOrderLookup({ status: "idle", data: null, error: null });
      return;
    }
    let cancelled = false;
    setOrderLookup({ status: "loading", data: null, error: null });
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/order/lookup/${encodeURIComponent(q)}`);
        if (!cancelled) setOrderLookup({ status: "ok", data, error: null });
      } catch (e) {
        if (!cancelled) {
          const msg = e.response?.status === 404 ? "notfound"
            : e.response?.status === 409 ? "ambiguous"
            : "error";
          setOrderLookup({ status: msg, data: null, error: msg });
        }
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [query]);

  // Focus auto sur l'input à l'ouverture
  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  // Fermeture ESC + blocage scroll body
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Normalise (retire accents + lowercase) pour matching accent-insensible
  const norm = (s) =>
    (typeof s === "string" ? s : "").normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase();

  const results = useMemo(() => {
    const raw = query.trim();
    if (raw.length < 2) return { products: [], gallery: [], faq: [] };
    // Découpe en mots : chaque mot doit être trouvé (AND) pour matcher.
    const tokens = norm(raw).split(/\s+/).filter(Boolean);
    const matches = (hay) => {
      const h = norm(hay);
      return tokens.every((tok) => h.includes(tok));
    };
    const anyField = (obj) =>
      obj && Object.values(obj).some((v) => typeof v === "string" && matches(v));

    const products = PRODUCTS.filter((p) => {
      const featuresAll = Object.values(p.features || {}).flat().join(" ");
      return (
        anyField(p.name) ||
        anyField(p.description) ||
        matches(p.slug) ||
        matches(p.category) ||
        matches(featuresAll)
      );
    }).slice(0, 6);

    const gallery = GALLERY
      .map((g, origIndex) => ({ ...g, origIndex }))
      .filter((g) => anyField(g.title))
      .slice(0, 6);

    const faqItems = (t.faq?.items || []).map((it, i) => ({ ...it, i }));
    const faq = faqItems.filter((it) => matches(it.q) || matches(it.a)).slice(0, 5);

    // Designs de croix (registry global peuplé par CrossConfigurator au mount
    // de la page produit). Match sur : id ("croix-simple-2"), label FR
    // ("Croix 2"), et family. Filtre les designs sans slug produit connu.
    const designRegistry = (typeof window !== "undefined" && window.__SIGNAPHARMA_CROSS_DESIGNS__) || [];
    const crossDesigns = designRegistry
      .filter((d) => {
        if (!CROSS_FAMILY_TO_PRODUCT_SLUG[d?.family]) return false;
        const labelFr = d.label?.fr || "";
        return matches(d.id || "") || matches(labelFr) || matches(d.family || "");
      })
      .slice(0, 8);

    return { products, gallery, faq, crossDesigns };
  }, [query, t]);

  const total = results.products.length + results.gallery.length + results.faq.length + (results.crossDesigns?.length || 0);

  const goProduct = (p) => {
    setOpen(false);
    setQuery("");
    navigate(`/produits/${p.category}/${p.slug}`);
  };

  const goCrossDesign = (design) => {
    setOpen(false);
    setQuery("");
    // Détermine la page produit "home" du design en se basant sur des
    // caractéristiques discriminantes (l'attribut `family` seul est ambigu :
    // family="croix" recouvre à la fois les designs customisables SVG de la
    // page classique ET les legacy PNG de la page néon).
    const id = String(design.id || "");
    const isDotmatrix = id.startsWith("dotmatrix-");
    const isEffetClassique = id.startsWith("classic-"); // presets d'effets non-personnalisables
    const isCustomizableSvg = design.customizableSvg === true;
    let slug;
    if (isDotmatrix) {
      slug = "croix-point-a-point";
    } else if (isCustomizableSvg || isEffetClassique) {
      // Designs "Croix N" personnalisables + effets → page CLASSIQUE
      slug = "croix-classique-led";
    } else {
      // Legacy croix PNG, croix-fleche, ronde, multi, angle, neon → page NÉON
      slug = CROSS_FAMILY_TO_PRODUCT_SLUG[design.family] || "croix-neon-led";
    }
    // ?design=X sera lu par CrossConfigurator pour préselectionner ce modèle.
    navigate(`/produits/croix-pharmacie/${slug}?design=${encodeURIComponent(design.id)}`);
  };

  const goGallery = (index = null) => {
    setOpen(false);
    setQuery("");
    if (index !== null && index !== undefined) {
      navigate(`/galerie#gallery-item-${index}`);
    } else {
      navigate("/galerie");
    }
  };

  const goFaq = (idx) => {
    setOpen(false);
    setQuery("");
    navigate(`/faq#item-${idx}`);
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        aria-label="Rechercher"
        title="Rechercher"
        className="p-2 text-zinc-300 hover:text-[#39FF14] transition-colors border border-transparent hover:border-[#39FF14]/30"
        data-testid="header-search-button"
      >
        <Search size={18} />
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-md flex items-start justify-center p-4 sm:p-10"
          onClick={() => setOpen(false)}
          data-testid="site-search-overlay"
        >
          <div
            className="w-full max-w-3xl bg-[#0a0a0a] border border-[#39FF14]/30 mt-4 sm:mt-10"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Input */}
            <div className="flex items-center gap-3 border-b border-white/10 px-5 py-4">
              <Search size={20} className="text-[#39FF14] shrink-0" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={lang === "fr"
                  ? "Produit, galerie, FAQ ou code de fiche technique (cs_… / DRAFT-…)"
                  : lang === "de" ? "Produkt, Galerie, FAQ oder Bestellcode"
                  : lang === "it" ? "Prodotto, galleria, FAQ o codice ordine"
                  : "Product, gallery, FAQ or order code"}
                className="flex-1 bg-transparent outline-none text-white text-lg font-body placeholder:text-zinc-600"
                data-testid="site-search-input"
              />
              <button
                onClick={() => setOpen(false)}
                className="p-1 text-zinc-500 hover:text-white transition-colors"
                aria-label="Fermer"
                data-testid="site-search-close"
              >
                <X size={20} />
              </button>
            </div>

            {/* Résultats */}
            <div className="max-h-[70vh] overflow-y-auto">
              {/* Recherche par code de fiche technique */}
              {orderLookup.status !== "idle" && (
                <section className="border-b border-white/5">
                  <h3 className="px-5 py-2 text-[10px] font-mono uppercase tracking-widest text-[#39FF14] flex items-center gap-2">
                    <Receipt size={12} /> {lang === "fr" ? "Code de fiche technique" : "Order code"}
                  </h3>
                  {orderLookup.status === "loading" && (
                    <div className="px-5 py-4 text-zinc-500 font-mono text-xs">
                      {lang === "fr" ? "Recherche en cours…" : "Searching…"}
                    </div>
                  )}
                  {orderLookup.status === "notfound" && (
                    <div className="px-5 py-4 text-zinc-500 font-mono text-xs" data-testid="site-search-order-notfound">
                      {lang === "fr" ? "Aucune commande trouvée pour ce code." : "No order found for this code."}
                    </div>
                  )}
                  {orderLookup.status === "ambiguous" && (
                    <div className="px-5 py-4 text-amber-400 font-mono text-xs">
                      {lang === "fr" ? "Code ambigu, précisez davantage de caractères." : "Ambiguous, refine your code."}
                    </div>
                  )}
                  {orderLookup.status === "ok" && orderLookup.data && (
                    <div className="px-5 py-4 bg-[#050505] border-y border-[#39FF14]/20" data-testid="site-search-order-found">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                            {orderLookup.data.payment_status === "paid"
                              ? (lang === "fr" ? "Commande payée" : "Paid order")
                              : (lang === "fr" ? "Commande en cours" : "Pending order")}
                          </div>
                          <div className="text-white font-display font-bold">
                            {orderLookup.data.customer_first_name || (lang === "fr" ? "Client" : "Customer")} · {orderLookup.data.items.length} article{orderLookup.data.items.length > 1 ? "s" : ""}
                          </div>
                        </div>
                        <div className="text-[#39FF14] font-mono text-sm">
                          {Math.round(orderLookup.data.amount_chf || 0).toLocaleString("fr-CH")} CHF
                        </div>
                      </div>
                      <ul className="space-y-2">
                        {orderLookup.data.items.map((it, i) => (
                          <li key={i} className="flex gap-3 items-center" data-testid={`site-search-order-item-${i}`}>
                            {it.preview_image ? (
                              <img src={it.preview_image} alt="" className="w-16 h-16 object-contain bg-black border border-[#39FF14]/30 shrink-0" />
                            ) : (
                              <div className="w-16 h-16 bg-[#0a0a0a] border border-white/10 shrink-0 flex items-center justify-center text-[9px] font-mono text-zinc-600 uppercase">
                                {lang === "fr" ? "Sans aperçu" : "No preview"}
                              </div>
                            )}
                            <div className="flex-1 min-w-0 text-xs">
                              <div className="text-white font-display font-semibold truncate">
                                {typeof it.product_name === "object" ? (it.product_name[lang] || it.product_name.fr) : it.product_name || it.product_slug}
                              </div>
                              <div className="text-zinc-500 font-mono text-[10px]">
                                {it.product_slug}{it.size_cm ? ` · ${it.size_cm} cm` : ""}{it.quantity > 1 ? ` · ×${it.quantity}` : ""}
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </section>
              )}

              {query.trim().length < 2 && (
                <div className="p-8 text-center text-zinc-500 font-mono text-sm">
                  {lang === "fr" ? "Tapez au moins 2 caractères pour rechercher."
                    : lang === "de" ? "Bitte mindestens 2 Zeichen eingeben."
                    : lang === "it" ? "Digita almeno 2 caratteri per cercare."
                    : "Type at least 2 characters."}
                </div>
              )}

              {query.trim().length >= 2 && total === 0 && (
                <div className="p-8 text-center text-zinc-500 font-mono text-sm" data-testid="site-search-empty">
                  {lang === "fr" ? `Aucun résultat pour « ${query} »`
                    : lang === "de" ? `Kein Ergebnis für „${query}"`
                    : lang === "it" ? `Nessun risultato per «${query}»`
                    : `No result for "${query}"`}
                </div>
              )}

              {results.products.length > 0 && (
                <section className="border-b border-white/5">
                  <h3 className="px-5 py-2 text-[10px] font-mono uppercase tracking-widest text-[#39FF14]">
                    {lang === "fr" ? "Produits" : lang === "de" ? "Produkte" : lang === "it" ? "Prodotti" : "Products"} · {results.products.length}
                  </h3>
                  <ul>
                    {results.products.map((p) => (
                      <li key={p.slug}>
                        <button
                          onClick={() => goProduct(p)}
                          className="w-full flex items-center gap-4 px-5 py-3 hover:bg-white/5 transition-colors text-left group"
                          data-testid={`site-search-product-${p.slug}`}
                        >
                          <img src={p.image} alt="" className="w-14 h-14 object-cover border border-white/10 shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="font-display font-semibold text-white truncate">
                              {p.name[lang] || p.name.fr}
                            </div>
                            <div className="text-xs text-zinc-500 font-mono truncate">
                              {(p.description[lang] || p.description.fr || "").slice(0, 80)}
                            </div>
                          </div>
                          <ArrowRight size={16} className="text-zinc-600 group-hover:text-[#39FF14] shrink-0" />
                        </button>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {results.crossDesigns && results.crossDesigns.length > 0 && (
                <section className="border-b border-white/5" data-testid="site-search-designs-section">
                  <h3 className="px-5 py-2 text-[10px] font-mono uppercase tracking-widest text-[#39FF14]">
                    {lang === "fr" ? "Modèles de croix" : lang === "de" ? "Kreuz-Modelle" : lang === "it" ? "Modelli di croce" : "Cross models"} · {results.crossDesigns.length}
                  </h3>
                  <ul className="grid grid-cols-2 sm:grid-cols-4 gap-2 p-3">
                    {results.crossDesigns.map((d) => {
                      const preview = d.imgColor || d.imgBody || d.previewUrl || null;
                      return (
                        <li key={d.id}>
                          <button
                            onClick={() => goCrossDesign(d)}
                            className="w-full flex flex-col items-center gap-1 p-2 border border-white/10 hover:border-[#39FF14]/60 transition-colors group text-center"
                            data-testid={`site-search-design-${d.id}`}
                            title={d.id}
                          >
                            <div className="w-full aspect-square bg-black flex items-center justify-center overflow-hidden">
                              {preview ? (
                                <img src={preview} alt={d.label?.fr || d.id} className="w-full h-full object-contain p-2" />
                              ) : (
                                <span className="text-[10px] font-mono text-[#39FF14]">+</span>
                              )}
                            </div>
                            <div className="text-xs font-display font-semibold text-white group-hover:text-[#39FF14] transition-colors truncate w-full">
                              {d.label?.fr || d.id}
                            </div>
                            <div className="text-[9px] font-mono text-zinc-500 truncate w-full">{d.id}</div>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </section>
              )}

              {results.gallery.length > 0 && (
                <section className="border-b border-white/5">
                  <h3 className="px-5 py-2 text-[10px] font-mono uppercase tracking-widest text-[#39FF14]">
                    {lang === "fr" ? "Galerie" : lang === "de" ? "Galerie" : lang === "it" ? "Galleria" : "Gallery"} · {results.gallery.length}
                  </h3>
                  <ul className="grid grid-cols-2 sm:grid-cols-3 gap-2 p-3">
                    {results.gallery.map((g, i) => (
                      <li key={g.origIndex ?? i}>
                        <button
                          onClick={() => goGallery(g.origIndex)}
                          className="w-full aspect-[4/3] relative overflow-hidden border border-white/10 hover:border-[#39FF14]/60 transition-colors group"
                          data-testid={`site-search-gallery-${g.origIndex ?? i}`}
                        >
                          <img src={g.url} alt={g.title[lang] || g.title.fr} className="w-full h-full object-cover" />
                          <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-colors flex items-end p-2">
                            <span className="text-[10px] font-mono text-white truncate">{g.title[lang] || g.title.fr}</span>
                          </div>
                        </button>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {results.faq.length > 0 && (
                <section>
                  <h3 className="px-5 py-2 text-[10px] font-mono uppercase tracking-widest text-[#39FF14]">
                    FAQ · {results.faq.length}
                  </h3>
                  <ul>
                    {results.faq.map((it) => (
                      <li key={it.i}>
                        <button
                          onClick={() => goFaq(it.i)}
                          className="w-full flex items-start gap-3 px-5 py-3 hover:bg-white/5 transition-colors text-left group"
                          data-testid={`site-search-faq-${it.i}`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="font-display font-semibold text-white text-sm">{it.q}</div>
                            <div className="text-xs text-zinc-500 mt-1 line-clamp-2">{it.a}</div>
                          </div>
                          <ArrowRight size={16} className="text-zinc-600 group-hover:text-[#39FF14] shrink-0 mt-1" />
                        </button>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </div>

            {/* Footer hint */}
            <div className="border-t border-white/10 px-5 py-2 flex items-center justify-between text-[10px] font-mono text-zinc-600 uppercase tracking-widest">
              <span>{lang === "fr" ? "ESC pour fermer"
                : lang === "de" ? "ESC zum Schliessen"
                : lang === "it" ? "ESC per chiudere"
                : "ESC to close"}</span>
              <span>{total > 0 ? `${total} ${lang === "fr" ? (total > 1 ? "résultats" : "résultat")
                : lang === "de" ? (total > 1 ? "Ergebnisse" : "Ergebnis")
                : lang === "it" ? (total > 1 ? "risultati" : "risultato")
                : (total > 1 ? "results" : "result")}` : ""}</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
